import {
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Popover,
  Stack,
  TextField,
} from '@mui/material';
import { ArrowDropDown, Check } from '@mui/icons-material';
import * as React from 'react';
import { Api } from '../../api';
import {
  PROGRAM_STAGE_FILTER_OPTIONS,
  PROGRAM_STATUS_OPTIONS,
} from '../../models/Program';

/** The member's latest program: the default, and how the filter worked before programs. */
const LATEST_PROGRAM = '';

type Props = {
  /** Program number, or '' for each member's latest program. */
  program: string;
  /** Program status, or '' for all. */
  status: string;
  onChange: (value: { program: string; status: string }) => void;
};

/**
 * Program Status filter: pick a program on the left (nothing is applied yet), then a status on
 * the right, which applies both and closes. The applied status is ticked, so clicking it again
 * changes only the program. Latest program + All is the default (no filter).
 */
export default function ProgramStatusFilter({
  program,
  status,
  onChange,
}: Props) {
  const programsApi = Api.Program.useReadCollectionAsAdmin();
  const programs = programsApi.data?.member ?? [];
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [draftProgram, setDraftProgram] = React.useState(program);

  const open = (el: HTMLElement) => {
    setDraftProgram(program);
    setAnchorEl(el);
  };
  const close = () => setAnchorEl(null);
  const apply = (nextStatus: string) => {
    onChange({ program: draftProgram, status: nextStatus });
    close();
  };

  const statusLabel =
    PROGRAM_STAGE_FILTER_OPTIONS.find((o) => o.value === status)?.label ??
    'All';
  const programTitle =
    programs.find((p) => String(p.number) === program)?.title ??
    `Program ${program}`;
  const display =
    program === LATEST_PROGRAM
      ? statusLabel
      : `${programTitle} · ${statusLabel}`;

  return (
    <>
      <TextField
        fullWidth
        label="Program Status"
        value={display}
        onClick={(e) => open(e.currentTarget)}
        onKeyDown={(e) => {
          if (['Enter', ' ', 'ArrowDown'].includes(e.key)) {
            e.preventDefault();
            open(e.currentTarget);
          }
        }}
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <ArrowDropDown sx={{ color: 'action.active' }} />
              </InputAdornment>
            ),
            sx: { cursor: 'pointer', '& input': { cursor: 'pointer' } },
          },
        }}
      />
      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Stack
          direction="row"
          divider={<Divider orientation="vertical" flexItem />}
        >
          <List
            dense
            sx={{ width: 220 }}
            subheader={<ListSubheader>Program</ListSubheader>}
          >
            <ListItemButton
              selected={draftProgram === LATEST_PROGRAM}
              onClick={() => setDraftProgram(LATEST_PROGRAM)}
            >
              <ListItemText primary="Latest program" />
            </ListItemButton>
            {programs.map((p) => (
              <ListItemButton
                key={p.id}
                selected={draftProgram === String(p.number)}
                onClick={() => setDraftProgram(String(p.number))}
                title={p.title}
              >
                <ListItemText
                  primary={p.title}
                  secondary={
                    p.status === 'published'
                      ? undefined
                      : PROGRAM_STATUS_OPTIONS.find((o) => o.value === p.status)
                          ?.label
                  }
                  primaryTypographyProps={{ noWrap: true }}
                />
              </ListItemButton>
            ))}
          </List>
          <List
            dense
            sx={{ width: 180 }}
            subheader={<ListSubheader>Status</ListSubheader>}
          >
            {PROGRAM_STAGE_FILTER_OPTIONS.map((o) => (
              <ListItemButton key={o.value} onClick={() => apply(o.value)}>
                <ListItemIcon sx={{ minWidth: 28 }}>
                  {o.value === status && <Check fontSize="small" />}
                </ListItemIcon>
                <ListItemText primary={o.label} />
              </ListItemButton>
            ))}
          </List>
        </Stack>
      </Popover>
    </>
  );
}
